import * as lib from '~/dependencies';
import log from './log';

/**
 * Returns media devices grouped by unique deviceId and kind (audioinput, audiooutput, video)
 * @param {Array} devices - An array of devices got from mediaDevices.enumerateDevices()
 * @param {String} kind - A string e.g: audioinput, audiooutput or video
 */
export function getMidiaDevicesByGroupId(devices, kind) {
  if (
    !lib._.isObject(devices) ||
    lib._.isEmpty(devices) ||
    lib._.isEmpty(kind)
  ) {
    return [];
  }

  const uniqueDevices = Array.from(new Set(devices.map(d => d.groupId))).map(
    groupId => {
      return devices.find(d => d.groupId === groupId && d.kind === kind);
    }
  );

  // Keep default devices on the first position
  const devicesSorted = uniqueDevices.sort(o => {
    if (o.deviceId === 'default') {
      return -1;
    }
    return 0;
  });

  // Remove undefined deviceIds
  return devicesSorted.filter(e => e && e.deviceId);
}

/**
 * Chromium and Electron expose virtual aggregate entries alongside the real devices: their
 * `deviceId` is `'default'` or `'communications'` and they always point at whatever the OS
 * currently considers the default. They are never pinnable - pinning one means "follow the OS",
 * which is expressed as the absence of a pin instead.
 */
export const VIRTUAL_DEVICE_IDS = ['default', 'communications'];

/**
 * The aggregate that stands for "whatever the OS considers default right now". Windows also
 * exposes 'communications' (its separate Default Communication Device), which is deliberately NOT
 * used: a user who sets a headset as the OS default has configured THAT one, and the two can point
 * at different hardware.
 */
export const SYSTEM_DEFAULT_DEVICE_ID = 'default';

/**
 * @name isVirtualDeviceId
 * @param {string} [deviceId]
 * @returns {boolean} true for Chromium's virtual aggregate ids.
 */
export function isVirtualDeviceId(deviceId) {
  return VIRTUAL_DEVICE_IDS.includes(deviceId);
}

/**
 * @name hasVirtualDefault
 * @description Whether the platform exposes the virtual OS-default aggregate for `kind`. True on
 * Chromium and Electron; false on Firefox and Safari, which enumerate no such entry - asking them
 * for it by id fails with OverconstrainedError.
 * @param {Array<MediaDeviceInfo>} devices - the result of enumerateDevices()
 * @param {'audioinput'|'audiooutput'} kind
 * @returns {boolean}
 */
export function hasVirtualDefault(devices, kind) {
  return (
    Array.isArray(devices) &&
    devices.some(
      d => d?.kind === kind && d.deviceId === SYSTEM_DEFAULT_DEVICE_ID
    )
  );
}

/**
 * @name toDeviceInfo
 * @description Convert a MediaDeviceInfo into a plain, serializable object.
 * MediaDeviceInfo has NO own enumerable properties - `deviceId`, `label` and `groupId` are
 * prototype getters - so both `{ ...info }` and `flatted.stringify(info)` yield `{}`. Every
 * device MUST pass through here before it reaches redux, or it is persisted as an empty object.
 * @param {MediaDeviceInfo | {deviceId: string, label: string, groupId: string}} [device]
 * @returns {{deviceId: string, label: string, groupId: string} | undefined}
 */
export function toDeviceInfo(device) {
  if (!device || !device.deviceId) {
    return undefined;
  }

  return {
    deviceId: device.deviceId,
    label: device.label || '',
    groupId: device.groupId || '',
  };
}

/**
 * @name stripDefaultPrefix
 * @description Drop the "Default - " / "Communications - " prefix Chromium puts on its virtual
 * entries, so a device reads the same whether it is currently the OS default or not. Matching on
 * ' - ' rather than the literal "Default - " because the prefix is localized.
 * @param {string} [label]
 * @returns {string} the label without its prefix, or '' when there is nothing usable.
 */
export function stripDefaultPrefix(label) {
  if (!label || typeof label !== 'string') {
    return '';
  }

  return label.includes(' - ') ? label.split(' - ')[1] : label;
}

/**
 * @name normalizeDeviceLabel
 * @description Comparable form of a device label: prefix stripped, trimmed and lowercased.
 * @param {string} [label]
 * @returns {string} the normalized label, or '' when there is nothing usable.
 */
export function normalizeDeviceLabel(label) {
  return stripDefaultPrefix(label)
    .trim()
    .toLowerCase();
}

/**
 * @name getSystemDefaultDevice
 * @description The device the OS considers default for `kind`, cross-browser and never
 * throwing. Chromium and Electron expose a virtual entry with `deviceId === 'default'`; Firefox
 * and Safari expose no such entry and instead enumerate the OS default first.
 * @param {Array<MediaDeviceInfo>} devices - the result of enumerateDevices()
 * @param {'audioinput'|'audiooutput'} kind
 * @returns {{deviceId: string, label: string, groupId: string} | undefined} undefined when the
 * platform has no device of that kind (e.g. Safari exposes no `audiooutput`).
 */
export function getSystemDefaultDevice(devices, kind) {
  if (!Array.isArray(devices) || !kind) {
    return undefined;
  }

  const ofKind = devices.filter(d => d && d.kind === kind && d.deviceId);

  if (ofKind.length === 0) {
    return undefined;
  }

  const virtualDefault = ofKind.find(d => d.deviceId === 'default');

  if (virtualDefault) {
    return toDeviceInfo(virtualDefault);
  }

  // Firefox/Safari: the first enumerated entry of the kind is the OS default.
  return toDeviceInfo(
    ofKind.find(d => !isVirtualDeviceId(d.deviceId)) || ofKind[0]
  );
}

/**
 * @name listRealAudioDevices
 * @description The pinnable audio devices of a kind, one per physical device, as plain objects.
 *
 * `getMidiaDevicesByGroupId` also collapses by `groupId`, but it keeps whichever entry it finds
 * first - and on Chromium the virtual "Default - X" alias shares its `groupId` with the real
 * device it points at, so that entry wins and the real device becomes unlistable. This prefers
 * the real entry for each group, which is what a picker needs: the aggregates are represented by
 * the explicit "System default" option instead, and every remaining row has a stable deviceId
 * that survives the OS default moving elsewhere.
 * @param {Array<MediaDeviceInfo>} devices
 * @param {'audioinput'|'audiooutput'} kind
 * @returns {Array<{deviceId: string, label: string, groupId: string}>}
 */
export function listRealAudioDevices(devices, kind) {
  if (!Array.isArray(devices) || !kind) {
    return [];
  }

  const byGroup = new Map();

  devices
    .filter(d => d && d.kind === kind && d.deviceId)
    .forEach(device => {
      // Devices without a groupId (Firefox does this) each stand on their own.
      const key = device.groupId || device.deviceId;
      const current = byGroup.get(key);

      if (
        !current ||
        (isVirtualDeviceId(current.deviceId) &&
          !isVirtualDeviceId(device.deviceId))
      ) {
        byGroup.set(key, device);
      }
    });

  return Array.from(byGroup.values())
    .filter(d => !isVirtualDeviceId(d.deviceId))
    .map(toDeviceInfo);
}

/**
 * @name matchPinnedDevice
 * @description Locate the still-connected device a stored pin refers to. The cascade goes
 * strongest signal first:
 *   1. `deviceId` - unique and unambiguous, and stable for as long as the origin's device-id
 *      salt lives (i.e. while the microphone permission grant persists).
 *   2. `groupId` - survives a deviceId change within the same salt: a replugged USB headset can
 *      re-enumerate under a new deviceId but the same groupId.
 *   3. normalized `label` - the only signal that survives a salt rotation (permission revoked
 *      then re-granted, private windows, and Firefox/Safari where ids are per-session). An OS
 *      locale change can move built-in device labels, but losing the match only degrades to the
 *      OS default, whereas dropping label matching would break every pin on Firefox/Safari on
 *      every restart.
 * Virtual entries are excluded from the candidates, so a pin captured while the device was
 * Chromium's "Default - Jabra" resolves to the REAL Jabra entry rather than re-pinning the
 * ephemeral 'default' id, and a match therefore always yields a stable, pinnable deviceId.
 * Matching is per-kind, which structurally prevents comparing a microphone against a speaker.
 * @param {Array<MediaDeviceInfo>} devices
 * @param {'audioinput'|'audiooutput'} kind
 * @param {{deviceId: string, label: string, groupId: string}} [pin]
 * @returns {{device: object, matchedBy: 'deviceId'|'groupId'|'label'} | undefined}
 */
export function matchPinnedDevice(devices, kind, pin) {
  if (!Array.isArray(devices) || !pin) {
    return undefined;
  }

  const candidates = devices.filter(
    d => d && d.kind === kind && d.deviceId && !isVirtualDeviceId(d.deviceId)
  );

  if (candidates.length === 0) {
    return undefined;
  }

  if (pin.deviceId && !isVirtualDeviceId(pin.deviceId)) {
    const byDeviceId = candidates.find(d => d.deviceId === pin.deviceId);

    if (byDeviceId) {
      return { device: toDeviceInfo(byDeviceId), matchedBy: 'deviceId' };
    }
  }

  if (pin.groupId) {
    const byGroupId = candidates.find(d => d.groupId === pin.groupId);

    if (byGroupId) {
      return { device: toDeviceInfo(byGroupId), matchedBy: 'groupId' };
    }
  }

  const wantedLabel = normalizeDeviceLabel(pin.label);

  if (wantedLabel) {
    const byLabel = candidates.find(
      d => normalizeDeviceLabel(d.label) === wantedLabel
    );

    if (byLabel) {
      return { device: toDeviceInfo(byLabel), matchedBy: 'label' };
    }
  }

  return undefined;
}

/**
 * @name resolveAudioDevice
 * @description Decide which device is in use for one role, and what its pin should become.
 *
 * A pin wins for as long as it still resolves. When it no longer does, the OS default becomes
 * active but the pin is KEPT, so unplugging a headset for an hour does not silently forget it -
 * replugging restores it with no user action.
 *
 * A pin pointing at a virtual aggregate id is normalized away to `null`, because pinning the
 * virtual default means "follow the OS". That also neutralizes the legacy data written before
 * pins were per-kind, where the untouched kind was copied from the `deviceId === 'default'`
 * entry and so was never a real choice.
 *
 * An empty enumeration for the kind (permission denied, no audio hardware, a USB hub that has
 * not settled yet) changes nothing at all: dropping pins there would wipe a user's setup on a
 * single unlucky boot.
 * @param {Array<MediaDeviceInfo>} devices
 * @param {'audioinput'|'audiooutput'} kind
 * @param {{deviceId: string, label: string, groupId: string}} [pin]
 * @returns {{device: object|undefined, pin: object|null}}
 */
export function resolveAudioDevice(devices, kind, pin) {
  const hasAnyOfKind =
    Array.isArray(devices) && devices.some(d => d?.kind === kind && d.deviceId);

  if (!hasAnyOfKind) {
    return { device: undefined, pin: pin || null };
  }

  const systemDefault = getSystemDefaultDevice(devices, kind);
  const pinnable = pin && pin.deviceId && !isVirtualDeviceId(pin.deviceId);

  if (!pinnable) {
    return { device: systemDefault, pin: null };
  }

  const match = matchPinnedDevice(devices, kind, pin);

  if (match) {
    return {
      device: match.device,
      // Re-anchor the pin onto the ids it actually resolved to, so the next lookup hits the
      // cheap deviceId branch instead of drifting further down the cascade, and so
      // `pin.deviceId === active.deviceId` stays a reliable "is pinned" test.
      pin: match.device,
    };
  }

  // The pinned device is gone: fall back to the OS default but remember the choice.
  return { device: systemDefault, pin };
}

/**
 * @name findConnectedDevice
 * @description The device of `kind` that is attached now and was not attached before, or
 * undefined when nothing was added.
 *
 * Deliberately a plain comparison of real deviceIds. The alternative - watching Chromium's
 * virtual 'default' entry for the OS default moving - cannot answer this: connecting a headset
 * does not always move the OS default onto it, and where it does, that entry keeps the same
 * constant deviceId and only its label and groupId follow, which the Electron build does not do
 * reliably. A device either is in the list or it is not, on every browser.
 *
 * Virtual aggregates are excluded: they are aliases rather than hardware, so they never "appear".
 * @param {Array<MediaDeviceInfo>} devices - the current enumerateDevices() result.
 * @param {'audioinput'|'audiooutput'} kind
 * @param {Array<string>} [knownIds] - the real deviceIds of `kind` seen on the previous check.
 * Pass null/undefined for "no baseline yet", which always yields undefined so the first check of
 * a call can never fire on the devices that were already there.
 * @returns {{deviceId: string, label: string, groupId: string} | undefined}
 */
export function findConnectedDevice(devices, kind, knownIds) {
  if (!Array.isArray(devices) || !Array.isArray(knownIds)) {
    return undefined;
  }

  const added = devices.find(
    d =>
      d?.kind === kind &&
      d.deviceId &&
      !isVirtualDeviceId(d.deviceId) &&
      !knownIds.includes(d.deviceId)
  );

  return toDeviceInfo(added);
}

/**
 * @name isSameDevice
 * @description Whether two stored devices refer to the same thing. Used to skip redundant
 * dispatches, so it treats two absent devices as equal.
 * @param {object} [a]
 * @param {object} [b]
 * @returns {boolean}
 */
export function isSameDevice(a, b) {
  if (!a && !b) {
    return true;
  }

  return !!a && !!b && a.deviceId === b.deviceId && a.label === b.label;
}

/**
 * @name buildAudioConstraints
 * @description Build `constraints.audio` for sip.js `sessionDescriptionHandlerOptions`.
 *
 *   pin resolved to `active`      -> `{ deviceId: { exact: active.deviceId } }`
 *   unpinned, aggregate available -> `{ deviceId: { exact: 'default' } }`
 *   neither                       -> `true`
 *
 * Every case that CAN name a device names it as a REQUIREMENT rather than a hint. A bare
 * (advisory) id is only scored: when the browser cannot satisfy it there is no error, it simply
 * hands over the closest other device - so the call runs on the built-in microphone while the
 * picker, the SIP header and redux all still name the headset. Every device bug in this area has
 * been that silent substitution rather than a failure.
 *
 * `audio: true` is the same failure mode with no id at all. It does NOT mean "the OS default":
 * Chromium resolves it through its own per-origin device preference - the sticky selection behind
 * the site's microphone setting - so a stale preference wins over the device the user just made
 * their OS default (NWA-3108). The virtual 'default' aggregate IS defined as "whatever the OS
 * considers default right now" and follows the default moving, so naming it explicitly keeps that
 * live re-routing while taking the choice away from the sticky preference. The speaker side has
 * always worked this way - `applyAudioSink` routes an unpinned element to the aggregate by id.
 *
 * `true` therefore survives only as the last resort: no resolved device at all, or a platform with
 * no aggregate to name (Firefox, Safari), where asking for 'default' by id would fail outright.
 * The unpinned branch keys off `active.deviceId` rather than the constant so the aggregate is only
 * requested where it was actually enumerated.
 *
 * The cost of `exact` is that an id which cannot be satisfied fails the whole acquisition. Three
 * things keep that bounded:
 *   - `createRetryingMediaStreamFactory` wraps every sip.js acquisition in a fallback ladder, so a
 *     recoverable failure never reaches `.invite()` / `.accept()` at all;
 *   - `resolveAudioConstraints` proves the id is enumerated immediately before the call, so a
 *     detached device is not requested in the first place;
 *   - the outgoing path probes the constraint first (`useDetectMicrophone`).
 *
 * `resolveAudioDevice` re-anchors a pin onto the device it resolved to, so comparing deviceIds
 * is enough to tell "the pin is in use" from "the pin fell back to the OS default". As a result
 * `{ exact: undefined }` is unreachable for any input.
 * @param {object} [pin] - system.userPreferences.audio[kind]
 * @param {object} [active] - system.deviceManager.audio[kind]
 * @returns {true | {deviceId: {exact: string}}}
 */
export function buildAudioConstraints(pin, active) {
  if (!active?.deviceId) {
    return true;
  }

  const isPinned =
    !!pin?.deviceId &&
    pin.deviceId === active.deviceId &&
    !isVirtualDeviceId(active.deviceId);

  if (isPinned) {
    return { deviceId: { exact: active.deviceId } };
  }

  // Unpinned, or pinned to hardware that is gone and so resolved onto the OS default.
  if (active.deviceId === SYSTEM_DEFAULT_DEVICE_ID) {
    return { deviceId: { exact: SYSTEM_DEFAULT_DEVICE_ID } };
  }

  return true;
}

/**
 * @name buildAudioConstraintLadder
 * @description The ordered constraints to try for one acquisition, most specific first. The single
 * definition of the fallback order, shared by the pre-call probe and by the media factory that
 * wraps sip.js, so the two can never disagree about what "falling back" means.
 *
 *   [ requested, { deviceId: { exact: 'default' } }, true ]
 *
 * The aggregate rung is dropped when the platform has none or when it IS the requested constraint,
 * and the trailing `true` is dropped when `requested` is already `true` - so the ladder never
 * repeats an attempt and an unconstrained request stays a single attempt.
 * @param {true | {deviceId: {exact: string}}} requested
 * @param {boolean} [aggregateAvailable] - whether `hasVirtualDefault` holds for the kind.
 * @returns {Array<true | {deviceId: {exact: string}}>} at least one rung.
 */
export function buildAudioConstraintLadder(requested, aggregateAvailable) {
  if (requested === true) {
    return [true];
  }

  const requestedId = requested?.deviceId?.exact;
  const ladder = [requested];

  if (aggregateAvailable && requestedId !== SYSTEM_DEFAULT_DEVICE_ID) {
    ladder.push({ deviceId: { exact: SYSTEM_DEFAULT_DEVICE_ID } });
  }

  ladder.push(true);

  return ladder;
}

/**
 * @name resolveAudioConstraints
 * @description The `constraints.audio` to hand sip.js for a call, reconciled against the live
 * enumeration, plus the ladder to fall back through if it cannot be opened.
 *
 * `buildAudioConstraints` decides from redux, and redux can be stale: a `devicechange` event that
 * never fired, or a headset unplugged since the last enumeration, both leave a pin resolved against
 * hardware that is no longer attached - and an exact id for a device that is not there fails the
 * acquisition. Enumerating costs about a millisecond, needs no permission and opens no device, so
 * it is affordable on both the incoming and the outgoing path - and unlike a getUserMedia probe it
 * cannot itself fail on Windows exclusive-mode drivers or delay the 200 OK.
 *
 * Staleness cuts both ways, so this also UPGRADES: when nothing is pinned and the platform
 * enumerates the aggregate, the aggregate is requested even if `deviceManager` has not resolved yet
 * (first call after a reload, or an enumeration that failed). Otherwise the very calls placed
 * before the device manager settles are the ones that keep the old `audio: true` behaviour.
 *
 * `usedFallback` means one thing only: THE PIN COULD NOT BE HONOURED. It is the caller's cue to
 * re-resolve the device (`phoneDeviceSetup`) and to drop the `X-User-Device-Info` header, because
 * the device stored in redux is not the one the call will use and must not be advertised. It is
 * never set for a call that was simply unpinned.
 * @param {object} [pin] - system.userPreferences.audio.microphone
 * @param {object} [active] - system.deviceManager.audio.microphone
 * @returns {Promise<{
 *   audio: true | {deviceId: {exact: string}},
 *   fallbacks: Array<true | {deviceId: {exact: string}}>,
 *   usedFallback: boolean,
 *   pinned: boolean,
 * }>} `fallbacks` is the rest of the ladder after `audio`, in order.
 */
export async function resolveAudioConstraints(pin, active) {
  const requested = buildAudioConstraints(pin, active);
  const requestedId = requested === true ? null : requested.deviceId.exact;
  // A pin, as opposed to the aggregate: only this needs proving, and only this can "fall back".
  const wantsPin = !!requestedId && !isVirtualDeviceId(requestedId);

  let devices;

  try {
    devices = await navigator.mediaDevices.enumerateDevices();
  } catch (err) {
    // Cannot prove anything is there, so nothing may be requested by id - and a pinned device
    // must not be advertised as the one in use.
    log.error('Failed to verify the audio devices before a call.', err);

    return {
      audio: true,
      fallbacks: [],
      usedFallback: wantsPin,
      pinned: false,
    };
  }

  const aggregateAvailable = hasVirtualDefault(devices, 'audioinput');
  const aggregate = aggregateAvailable
    ? { deviceId: { exact: SYSTEM_DEFAULT_DEVICE_ID } }
    : true;

  const withLadder = (audio, extra) => ({
    audio,
    fallbacks: buildAudioConstraintLadder(audio, aggregateAvailable).slice(1),
    ...extra,
  });

  if (wantsPin) {
    const stillAttached = devices.some(
      d => d.kind === 'audioinput' && d.deviceId === requestedId
    );

    return stillAttached
      ? withLadder(requested, { usedFallback: false, pinned: true })
      : withLadder(aggregate, { usedFallback: true, pinned: false });
  }

  // Unpinned. `aggregate` rather than `requested`, so a redux state that has not resolved yet still
  // names the OS default instead of leaving the choice to `audio: true`.
  return withLadder(aggregate, { usedFallback: false, pinned: false });
}

/**
 * @name buildDeviceInfoHeader
 * @description Build a sanitized `X-User-Device-Info` SIP header line from a raw
 * device label (e.g. the selected microphone's MediaDeviceInfo.label). Strips the
 * "Default - " prefix used on multi-language systems, removes control characters and
 * any characters that could break SIP header parsing, and caps the length. Returns
 * `undefined` when there is no usable label so callers can skip adding the header.
 * @param {string} [label] - The device label.
 * @returns {string | undefined} e.g. "X-User-Device-Info: Logitech USB Headset"
 */
export function buildDeviceInfoHeader(label) {
  if (!label || typeof label !== 'string') {
    return undefined;
  }

  const sanitized = stripDefaultPrefix(label)
    // Remove Unicode control chars (incl. CR/LF) to prevent header injection.
    .replace(/\p{Cc}/gu, '')
    // Remove characters that break SIP header parsing.
    .replace(/[,;:]/g, ' ')
    // Collapse whitespace.
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 128);

  if (!sanitized) {
    return undefined;
  }

  return `X-User-Device-Info: ${sanitized}`;
}

/**
 * @name sdpModifierCodec
 * @description Change the SDP to always negotiate Opus and G.711 respectively.
 * @param {string} description
 * @returns Promise<string>
 */
export function sdpModifierCodec(description) {
  description.sdp = description.sdp.replace(
    'UDP/TLS/RTP/SAVPF 111 63 103 104 9 0 8 106 105 13 110 112 113 126',
    'RTP/SAVPF 111 8 103 104 9 0 63 106 105 13 110 112 113 126'
  );

  return Promise.resolve(description);
}

/**
 * @name sdpModifierCodec
 * @description Store Media Stream Globally at Window
 * @param {object [MediaStream]} mediaStreamRef
 * @returns void
 */
function register(stream) {
  if (!Array.isArray(window.streamReferenceList)) {
    window.streamReferenceList = [];
  }

  if (Array.isArray(window.streamReferenceList)) {
    window.streamReferenceList.push(stream);
  }
}

function clear() {
  const { streamReferenceList } = window;

  if (Array.isArray(window.streamReferenceList)) {
    streamReferenceList.forEach(stream => {
      stream.getTracks().forEach(track => track.stop());
    });

    window.streamReferenceList = [];
  }
}

export const mediaStreamManager = {
  clear,
  register,
};

/**
 * Check for at least one video device
 */
export async function checkVideoDevice() {
  const devices = await navigator.mediaDevices.enumerateDevices();

  const hasVideoDevice = devices.find(e => e.kind === 'videoinput');

  if (hasVideoDevice) {
    return true;
  }

  return false;
}

/**
 * @name getLocalAudioStats
 * @description Reads a one-shot snapshot of the LOCAL microphone audio stats from a peer
 * connection's audio sender. A single getStats() on the audio sender yields both the
 * `media-source` report (the local mic: audioLevel, totalAudioEnergy, totalSamplesDuration)
 * and the `outbound-rtp` report (bytesSent, packetsSent), plus the live track state. Used to
 * detect "access granted but no audio" (dead microphone) a few seconds after a call connects.
 * @param {RTCPeerConnection} pc
 * @returns {Promise<import('./reportMediaError').LocalAudioStats|null>} flattened stats, or null
 * when no audio sender/track exists
 */
export async function getLocalAudioStats(pc) {
  if (!pc || typeof pc.getSenders !== 'function') return null;

  const sender = pc.getSenders().find(s => s.track && s.track.kind === 'audio');

  if (!sender || !sender.track) return null;

  const { track } = sender;
  const settings = track.getSettings ? track.getSettings() : {};

  const result = {
    deviceLabel: track.label || null,
    deviceId: settings.deviceId || null,
    trackMuted: track.muted,
    trackEnabled: track.enabled,
    trackReadyState: track.readyState,
    audioLevel: null,
    totalAudioEnergy: null,
    totalSamplesDuration: null,
    bytesSent: null,
    packetsSent: null,
  };

  const stats = await sender.getStats();

  stats.forEach(report => {
    if (report.type === 'media-source' && report.kind === 'audio') {
      result.audioLevel = report.audioLevel;
      result.totalAudioEnergy = report.totalAudioEnergy;
      result.totalSamplesDuration = report.totalSamplesDuration;
    } else if (report.type === 'outbound-rtp') {
      result.bytesSent = report.bytesSent;
      result.packetsSent = report.packetsSent;
    }
  });

  return result;
}

/**
 * @name enableSenderTracks
 * @description Enable/disable the outgoing tracks of a peer connection.
 * @param {RTCPeerConnection} pc
 * @param {boolean} enabled
 */
export function enableSenderTracks(pc, enabled) {
  pc.getSenders().forEach(({ track }) => {
    if (track) {
      track.enabled = enabled;
    }
  });
}

/**
 * Web Audio graph that mixes a 3-way conference.
 *
 * The graph replaces the outgoing track of BOTH legs with a mixed track, so it must be
 * owned outside of the code that builds it: whoever splits or ends the conference has to
 * restore the original microphone tracks, stop the mixed tracks and close the AudioContext.
 * Without that teardown the mixed tracks stay enabled (each participant keeps hearing the
 * other after a split) and the AudioContext keeps holding the microphone track open after
 * the calls end.
 *
 * Only one conference can be mixed at a time, so the graph is kept in a module singleton.
 */
let conferenceMixerState = null;

/**
 * @name createConferenceMixer
 * @description Build the mixing graph for a 3-way conference and swap the mixed tracks
 * onto both peer connections. What participant 1 hears is `microphone + participant 2`,
 * what participant 2 hears is `microphone + participant 1`.
 * @param {RTCPeerConnection} pcSession1
 * @param {RTCPeerConnection} pcSession2
 * @returns {Promise<MediaStream|null>} the remote streams of both legs, to be played
 * locally, or null when either leg has no audio sender.
 */
async function createConferenceMixer(pcSession1, pcSession2) {
  // Never stack a new graph on top of a live one, otherwise the previous mixed track is
  // picked up as if it were the microphone and the mix feeds back into itself.
  await destroyConferenceMixer();

  const senderSession1 = pcSession1
    ?.getSenders()
    .find(s => s.track?.kind === 'audio');
  const senderSession2 = pcSession2
    ?.getSenders()
    .find(s => s.track?.kind === 'audio');

  if (!senderSession1 || !senderSession2) {
    log.error('Failed to mix conference: an audio sender is missing.');
    return null;
  }

  // The real microphone tracks, captured BEFORE replaceTrack() so they can be restored.
  const micTrackSession1 = senderSession1.track;
  const micTrackSession2 = senderSession2.track;
  const micEnabled = micTrackSession1.enabled;

  const localStream = new MediaStream([micTrackSession1]);
  const remoteStreamSession1 = new MediaStream();
  const remoteStreamSession2 = new MediaStream();
  // Both remote legs, played through the local audio element.
  const mixer = new MediaStream();

  pcSession1.getReceivers().forEach(({ track }) => {
    if (track) {
      remoteStreamSession1.addTrack(track);
      mixer.addTrack(track);
    }
  });

  pcSession2.getReceivers().forEach(({ track }) => {
    if (track) {
      remoteStreamSession2.addTrack(track);
      mixer.addTrack(track);
    }
  });

  const audioContext = new AudioContext();
  const micSource = audioContext.createMediaStreamSource(localStream);
  const participant1 = audioContext.createMediaStreamSource(
    remoteStreamSession1
  );
  const participant2 = audioContext.createMediaStreamSource(
    remoteStreamSession2
  );

  // Mute has to be applied here while the conference is up: the senders no longer carry
  // the microphone track, so disabling them would also cut the other participant out.
  const micGain = audioContext.createGain();
  micGain.gain.value = micEnabled ? 1 : 0;
  micSource.connect(micGain);

  const mixForSession1 = audioContext.createMediaStreamDestination();
  const mixForSession2 = audioContext.createMediaStreamDestination();

  micGain.connect(mixForSession1);
  participant2.connect(mixForSession1);

  micGain.connect(mixForSession2);
  participant1.connect(mixForSession2);

  const [mixedTrackSession1] = mixForSession1.stream.getAudioTracks();
  const [mixedTrackSession2] = mixForSession2.stream.getAudioTracks();

  conferenceMixerState = {
    audioContext,
    micGain,
    micEnabled,
    // micSource is kept on its own so the microphone can be swapped mid-conference without
    // rebuilding the graph. See replaceConferenceMicrophone.
    micSource,
    participants: [participant1, participant2],
    mixedTracks: [mixedTrackSession1, mixedTrackSession2],
    legs: [
      { sender: senderSession1, originalTrack: micTrackSession1 },
      { sender: senderSession2, originalTrack: micTrackSession2 },
    ],
  };

  await Promise.all([
    senderSession1.replaceTrack(mixedTrackSession1),
    senderSession2.replaceTrack(mixedTrackSession2),
  ]);

  return mixer;
}

/**
 * @name destroyConferenceMixer
 * @description Tear the mixing graph down: put the microphone tracks back on both
 * senders, stop the mixed tracks and close the AudioContext. Safe to call when no
 * conference is mixed.
 * @returns {Promise<void>}
 */
async function destroyConferenceMixer() {
  const state = conferenceMixerState;

  if (!state) {
    return;
  }

  // Cleared upfront so a second teardown (e.g. split followed by hangup) is a no-op.
  conferenceMixerState = null;

  // Never reject: callers tear the mix down on the way to hanging up a call.
  try {
    // Restore the microphone BEFORE stopping the mixed tracks, otherwise the leg that
    // survives a split is left with an ended track and goes silent.
    await Promise.all(
      state.legs.map(async ({ sender, originalTrack }) => {
        if (!originalTrack || originalTrack.readyState !== 'live') {
          return;
        }

        try {
          originalTrack.enabled = state.micEnabled;
          await sender.replaceTrack(originalTrack);
        } catch (err) {
          // Expected when the leg already hung up and its peer connection is closed.
          log.info('Could not restore the microphone track on a sender.', err);
        }
      })
    );

    state.mixedTracks.forEach(track => track?.stop());
    [state.micSource, state.micGain, ...state.participants].forEach(node =>
      node.disconnect()
    );

    await state.audioContext.close();
  } catch (err) {
    log.error('Failed to tear down the conference mix.', err);
  }
}

/**
 * @name setConferenceMixerMicEnabled
 * @description Mute/unmute the local microphone inside the conference mix.
 * @param {boolean} enabled
 * @returns {boolean} true when the conference mix handled it.
 */
function setConferenceMixerMicEnabled(enabled) {
  if (!conferenceMixerState) {
    return false;
  }

  conferenceMixerState.micEnabled = enabled;
  conferenceMixerState.micGain.gain.value = enabled ? 1 : 0;
  conferenceMixerState.legs.forEach(({ originalTrack }) => {
    if (originalTrack && originalTrack.readyState === 'live') {
      originalTrack.enabled = enabled;
    }
  });

  return true;
}

/**
 * @name replaceConferenceMicrophone
 * @description Swap the microphone feeding a live conference mix.
 *
 * While a conference is up the senders carry mixed tracks, not the microphone, so calling
 * `replaceTrack` on them would tear the mix down and each participant would stop hearing the
 * other. The new microphone is spliced into the Web Audio graph instead, upstream of the gain
 * node that owns mute, and both legs' `originalTrack` is repointed at it so a later split or
 * hangup restores the NEW microphone rather than the one that has since been stopped.
 * @param {MediaStreamTrack} newTrack
 * @returns {boolean} true when a conference mix handled it.
 */
function replaceConferenceMicrophone(newTrack) {
  const state = conferenceMixerState;

  if (!state || !newTrack) {
    return false;
  }

  try {
    // Captured before the legs are repointed. Both legs acquired their own microphone stream,
    // so there can be two tracks to release here.
    const previousTracks = state.legs
      .map(({ originalTrack }) => originalTrack)
      .filter(track => track && track !== newTrack);

    state.micSource.disconnect();

    const micSource = state.audioContext.createMediaStreamSource(
      new MediaStream([newTrack])
    );
    micSource.connect(state.micGain);

    newTrack.enabled = state.micEnabled;
    state.micSource = micSource;
    state.legs.forEach(leg => {
      leg.originalTrack = newTrack;
    });

    previousTracks.forEach(track => track.stop());

    return true;
  } catch (err) {
    log.error('Failed to replace the microphone in the conference mix.', err);
    return false;
  }
}

export const conferenceMixer = {
  create: createConferenceMixer,
  destroy: destroyConferenceMixer,
  setMicEnabled: setConferenceMixerMicEnabled,
  replaceMicrophone: replaceConferenceMicrophone,
  isActive: () => conferenceMixerState !== null,
};

/**
 * @name isPinInUse
 * @description Whether the device a role is pinned to is the one actually in use. The pickers
 * need this rather than "is the pinned device attached", so a pin that is plugged in but did not
 * win - the device is busy, or moving the calls onto it failed - is not shown as selected.
 * `resolveAudioDevice` re-anchors a pin onto whatever it matched, so comparing ids is enough.
 * @param {object} [pin]
 * @param {object} [active]
 * @returns {boolean}
 */
export function isPinInUse(pin, active) {
  return !!pin?.deviceId && pin.deviceId === active?.deviceId;
}

/**
 * @name applyAudioSink
 * @description Route one media element's audio to an output device, cross-browser and never
 * throwing.
 *
 * Call this every time the element gets a new `srcObject`, not once per device change. Assigning
 * `srcObject` runs the media load algorithm and builds a NEW player for the element, and a sink
 * assigned while the element had no media does not reliably carry over to it - which is exactly
 * the startup case, where the speaker resolves long before the first call is answered. A single
 * application also has no recovery: `setSinkId` rejects while the outputs are still settling (a
 * Bluetooth headset connecting during startup) or before the microphone permission grant lands,
 * and with nothing to re-apply it the element then plays out of the OS default for the rest of
 * the session while the picker still shows the pinned device.
 *
 * Deliberately does NOT skip when `element.sinkId` already equals `deviceId`: the element reports
 * the id it was last given, which is the very thing that cannot be trusted after a new player was
 * built. Re-applying an id that is genuinely in use is a no-op in the browser.
 * @param {HTMLMediaElement} [element]
 * @param {string} [deviceId] - a resolved `deviceManager.audio.*` id. Legitimately absent on
 * Safari, which enumerates no audio outputs at all.
 * @returns {Promise<boolean>} whether the element is now playing out of `deviceId`.
 */
export async function applyAudioSink(element, deviceId) {
  // Firefox and Safari have no setSinkId, and with no resolved device there is nothing to route:
  // the element follows the OS default, which is the correct behaviour for both.
  if (!element || !deviceId || typeof element.setSinkId !== 'function') {
    return false;
  }

  try {
    await element.setSinkId(deviceId);

    return true;
  } catch (err) {
    log.error(`Failed to route the audio to the device ${deviceId}.`, err);

    return false;
  }
}
